import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import disputeService from '../services/disputeService';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ROUTES } from '../utils/constants';

// Only an applicant can raise a dispute, about their own application (DisputePolicy@create) — that
// flow already lives in ApplicantApplicationPage. This page is the other side of it: owner/admin
// reviewing and acting on disputes already raised for one specific application (the API has no
// cross-application listing, so there's no "all my disputes" view — see ROUTES.DISPUTES).
const STATUS_LABELS = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  investigating: 'Investigating',
  corrected: 'Corrected',
  upheld: 'Upheld',
  escalated: 'Escalated'
};

const TYPE_LABELS = {
  factual_error: 'Factual error',
  incomplete_data: 'Incomplete data',
  incorrect_source: 'Incorrect source',
  other: 'Other'
};

const messageFrom = (err, fallback) => {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat().join(' ');
  return err.response?.data?.message || err.message || fallback;
};

const ResolveForm = ({ dispute, onResolve, busy }) => {
  const [status, setStatus] = useState('corrected');
  const [outcome, setOutcome] = useState('');
  const [evidence, setEvidence] = useState('');

  return (
    <form
      className="resolve-form"
      onSubmit={(e) => {
        e.preventDefault();
        onResolve(dispute.id, { status, outcome, evidence });
      }}
      style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}
    >
      <div className="form-group">
        <label className="form-label">Outcome</label>
        <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="corrected">Corrected — the dispute was right, we fixed it</option>
          <option value="upheld">Upheld — the original information was correct</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">What changed (or why it stands)</label>
        <input className="form-control" value={outcome} onChange={(e) => setOutcome(e.target.value)} required />
      </div>
      <div className="form-group">
        <label className="form-label">Evidence</label>
        <textarea className="form-control" value={evidence} onChange={(e) => setEvidence(e.target.value)} required />
      </div>
      <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Resolve dispute'}</button>
    </form>
  );
};

const DisputesPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [disputes, setDisputes] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setDisputes(await disputeService.getDisputes(id));
    } catch (err) {
      setLoadError(messageFrom(err, 'Could not load disputes for this application.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (action, disputeId, data) => {
    setBusyId(disputeId);
    setNotice(null);
    try {
      if (action === 'acknowledge') await disputeService.acknowledgeDispute(disputeId);
      if (action === 'investigate') await disputeService.investigateDispute(disputeId, data);
      if (action === 'resolve') await disputeService.resolveDispute(disputeId, data);
      setNotice({ type: 'success', message: 'Updated.' });
      await load();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'That action could not be completed.') });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="disputes-page" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <p><Link to={`${ROUTES.APPLICATIONS}/${id}`}>&larr; Back to application</Link></p>
      <h1>Disputes</h1>
      {loadError && <Alert type="error" message={loadError} />}
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {!loadError && disputes.length === 0 && (
        <div className="card"><div className="card-body">No disputes have been raised for this application.</div></div>
      )}

      {disputes.map((dispute) => (
        <div className="card" key={dispute.id} style={{ marginBottom: 16 }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <strong>{TYPE_LABELS[dispute.dispute_type] || dispute.dispute_type}</strong>
              <span className={`status-badge status-badge--${['corrected', 'upheld'].includes(dispute.status) ? 'success' : 'warning'}`}>
                {STATUS_LABELS[dispute.status] || dispute.status}
              </span>
            </div>
            <p style={{ marginTop: 8 }}>{dispute.description}</p>

            {dispute.status === 'submitted' && (
              <button className="btn-secondary" disabled={busyId === dispute.id} onClick={() => act('acknowledge', dispute.id)}>
                {busyId === dispute.id ? 'Saving…' : 'Acknowledge'}
              </button>
            )}
            {dispute.status === 'acknowledged' && (
              <button className="btn-secondary" disabled={busyId === dispute.id} onClick={() => act('investigate', dispute.id)}>
                {busyId === dispute.id ? 'Saving…' : 'Start investigating'}
              </button>
            )}
            {dispute.status === 'investigating' && (
              <ResolveForm dispute={dispute} busy={busyId === dispute.id} onResolve={(disputeId, data) => act('resolve', disputeId, data)} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
export default DisputesPage;
